import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Alert from "veneer/Alert";
import Box from "veneer/Box";

import { Application } from "cortex/ui/src/fleet/view/application/model";
import ConfigureParams from "cortex/ui/src/fleet/view/ConfigureParams";
import { Network } from "cortex/ui/src/network/model";
import {
  getSystemManagedParams,
  isTesting,
} from "cortex/ui/src/redux-store/devices/selectors";
import { NavLoader } from "cortex/ui/src/ui/loader";
import { PRIORITY } from "cortex/ui/src/ui/parameter-form/utils";
import { RefreshView } from "cortex/ui/src/ui/refresh-view";

import { Device } from "./model";

type SubmittedParam = {
  appId?: number;
  name: string;
  value: unknown;
};

interface ConfigureDeviceParamsProps {
  addErrorMessage: (msg: string) => void;
  apps: Application[];
  device: Device;
  network: Network | null;
  readonly: boolean;
  updateParams: (args: {
    id: number;
    params: Device["user_params"];
  }) => Promise<void>;
}

export default function ConfigureDeviceParams({
  device,
  network,
  updateParams,
  ...rest
}: ConfigureDeviceParamsProps) {
  const [refresh, setRefresh] = useState(false);
  const { device_id, id } = device;
  const isTestDevice = useMemo(() => isTesting(device) ?? false, [device]);

  const successLink = useMemo(() => {
    return <Link to={`/devices/${device_id}/overview`}>View Device</Link>;
  }, [device_id]);

  const systemManagedParams = useMemo(
    () => getSystemManagedParams(device),
    [device],
  );
  const deviceParams = device?.user_params ?? [];

  const onSubmit = useCallback(
    (params: SubmittedParam[]) => {
      return updateParams({
        id,
        params: params as unknown as Device["user_params"],
      }).then(() => {
        setRefresh(true);
      });
    },
    [id, updateParams],
  );

  if (refresh) {
    return <RefreshView />;
  }

  if (!network) {
    return <NavLoader.Proxy />;
  }

  // Ensure state is correct, so downstream components don't suffer
  // synchronization problems.
  if (device.network_id !== network.id) {
    return <NavLoader.Proxy>Syncing device network...</NavLoader.Proxy>;
  }

  return (
    <div>
      <Box mb={2}>
        <Alert dense severity="info">
          <span>
            You&#39;re currently configuring a single device. To configure all
            devices for this network, you may configure the&nbsp;
            <Link to={`/networks/${network.id}/configure`}>network level</Link>
            &nbsp;instead.
          </span>
        </Alert>
      </Box>
      <ConfigureParams
        {...rest}
        deviceParams={[...deviceParams]}
        network={network}
        onSubmit={onSubmit}
        priority={PRIORITY.DEVICE}
        showTestingApps={isTestDevice}
        successLink={successLink}
        systemManagedParams={systemManagedParams}
      />
    </div>
  );
}
